import styled from 'styled-components';

import { SMSContentDefinition, SMSSender } from 'modules/graphql/types';
import { useHasMovableInkTile } from 'modules/templateEditor/common/hooks';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { IconSuffixWrapper } from 'modules/templateEditor/common/Controls/UrlInputFieldIcons';
import { CreativeLibraryIconButton } from 'modules/templateEditor/common/Controls/CreativeLibraryIconButton';
import { MovableInkIconButton } from 'modules/templateEditor/common/Controls/MovableInkIconButton';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { WarningFill } from 'ui/icons';
import { AVATAR_SIZES } from 'ui/avatar';
import { Body2, spacing, textColors } from 'ui/theme';

import {
  ContactCardFormWrapper,
  ContactCardHeaderCard,
  ContactCardImageHelpTextWrapper,
  StyledBasicFilterSelect,
  StyledWebLink,
  StyledInfoBanner,
  StyledOpenNewWindow16,
  ErrorTextWrapper,
} from './ContactCards.styles';

import { formatSMSSenderOptions, getContactCardAvatar, isHandlebarsInput } from '../utils';
import { ContactCard } from '../types';
import { useContactCardContext } from '../contexts/ContactCardContext';

export const StyledWrapper = styled.div`
  padding: ${spacing.eighth} ${spacing.threeEights};
`;

export const SecondaryText = styled.span`
  color: ${textColors.textSecondary};
`;

const StyledIconSuffixWrapper = styled(IconSuffixWrapper)`
  margin-right: ${spacing.half};
`;

const DisplayNameWrapper = styled.div`
  max-width: 400px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledInput = styled(InputText)`
  && {
    > input {
      line-height: ${spacing.base};
      padding: ${spacing.fiveEights} ${spacing.threeQuarter};
    }
  }
`;

const ContactCardEditable = () => {
  const {
    smsSenders,
    assetDetails: contactCardDetails,
    assetErrors: contactCardErrors,
    setChangesMade,
    updateAssetDetails: updateContactCardDetails,
  } = useContactCardContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { smsSender } = (liveTemplate?.mediumData as SMSContentDefinition) || {};
  const hasDataFeeds = liveTemplate.dataFeedIds?.length > 0;
  const hasMovableInkTile = useHasMovableInkTile();

  const handleFieldChange = (fieldName: string, newValue: string | SMSSender | undefined) => {
    updateContactCardDetails({
      ...contactCardDetails,
      [fieldName]: newValue,
    });
    setChangesMade(true);
  };

  const renderFieldError = (errorMessage: string | undefined) =>
    errorMessage && (
      <ErrorTextWrapper>
        <WarningFill isColorInherited />
        {errorMessage}
      </ErrorTextWrapper>
    );

  return (
    <StyledWrapper>
      {hasDataFeeds && (
        <StyledInfoBanner>Data feeds cannot be used in contact card fields</StyledInfoBanner>
      )}
      <FormField
        data-test="contact-card-form-company-name"
        labelText="Company name"
        hasError={!!contactCardErrors?.displayName}
        errorText={renderFieldError(contactCardErrors?.displayName)}
        isRequired
      >
        <StyledInput
          id="contact-card-company-name"
          value={contactCardDetails?.displayName || ''}
          onChange={(val) => handleFieldChange('displayName', val)}
          hasIcon={false}
        />
      </FormField>
      <FormField
        data-test="contact-card-form-sender"
        labelText="Sender"
        hasError={!!contactCardErrors?.smsSender}
        errorText={renderFieldError(contactCardErrors?.smsSender)}
        isRequired
      >
        <StyledBasicFilterSelect
          id="contact-cards-sender-select"
          options={formatSMSSenderOptions(smsSenders)}
          placeholder="Select a sender..."
          onSelect={([selected]) =>
            handleFieldChange(
              'smsSender',
              smsSenders.find((sender) => sender.id === selected),
            )
          }
          isFullWidth
          defaultValues={[contactCardDetails.smsSender?.id || smsSender?.id]}
        />
      </FormField>
      <FormField data-test="contact-card-form-website" labelText="Website">
        <StyledInput
          id="contact-card-website"
          value={contactCardDetails?.website || ''}
          onChange={(val) => handleFieldChange('website', val)}
        />
      </FormField>
      <FormField
        data-test="contact-card-form-image-url"
        labelText="Image URL"
        helpText={
          <ContactCardImageHelpTextWrapper>
            Supports images less than 5MB.
          </ContactCardImageHelpTextWrapper>
        }
        hasError={!!contactCardErrors?.imageUrl}
        errorText={renderFieldError(contactCardErrors?.imageUrl)}
      >
        <StyledInput
          id="contact-card-image-url"
          value={contactCardDetails?.imageUrl || ''}
          onChange={(val) => handleFieldChange('imageUrl', val)}
          hasIcon={false}
          Suffix={
            <StyledIconSuffixWrapper>
              <CreativeLibraryIconButton
                onSelect={(val) => handleFieldChange('imageUrl', val)}
                url={contactCardDetails?.imageUrl}
              />
              {hasMovableInkTile && (
                <MovableInkIconButton onSelect={(val) => handleFieldChange('imageUrl', val)} />
              )}
            </StyledIconSuffixWrapper>
          }
        />
      </FormField>
      <FormField data-test="contact-card-form-subheader" labelText="Subheader">
        <StyledInput
          id="contact-card-subheader"
          value={contactCardDetails?.subheader || ''}
          onChange={(val) => handleFieldChange('subheader', val)}
        />
      </FormField>
      <FormField
        data-test="contact-card-form-email"
        hasError={!!contactCardErrors?.email}
        errorText={renderFieldError(contactCardErrors?.email)}
        labelText="Company email"
      >
        <StyledInput
          id="contact-card-email"
          value={contactCardDetails?.email || ''}
          onChange={(val) => handleFieldChange('email', val)}
          hasIcon={false}
        />
      </FormField>
    </StyledWrapper>
  );
};

const ContactCardDetails = () => {
  const { assetDetails: contactCardDetails } = useContactCardContext();
  const noValueText = <SecondaryText>None</SecondaryText>;
  const getImgUrlDetails = (imageUrl?: string) => {
    if (!imageUrl) return noValueText;
    if (isHandlebarsInput(imageUrl)) return <>{`${imageUrl}`}</>;
    return (
      <StyledWebLink href={imageUrl}>
        {imageUrl} <StyledOpenNewWindow16 isColorInherited />
      </StyledWebLink>
    );
  };

  return (
    <>
      <FormField
        data-test="contact-card-form-company-name"
        labelText="Company name"
        isRequired
        isHorizontalLayout
      >
        <Body2>{contactCardDetails?.displayName}</Body2>
      </FormField>
      <FormField
        data-test="contact-card-form-sender"
        labelText="Sender"
        isRequired
        isHorizontalLayout
      >
        <Body2>{contactCardDetails?.smsSender?.name}</Body2>
      </FormField>
      <FormField data-test="contact-card-form-website" labelText="Website" isHorizontalLayout>
        <Body2>{contactCardDetails?.website || noValueText}</Body2>
      </FormField>
      <FormField data-test="contact-card-form-image-url" labelText="Image URL" isHorizontalLayout>
        {getImgUrlDetails(contactCardDetails?.imageUrl)}
      </FormField>
      <FormField data-test="contact-card-form-subheader" labelText="Subheader" isHorizontalLayout>
        <Body2>{contactCardDetails?.subheader || noValueText}</Body2>
      </FormField>
      <FormField data-test="contact-card-form-email" labelText="Company email" isHorizontalLayout>
        <Body2>{contactCardDetails?.email || noValueText}</Body2>
      </FormField>
    </>
  );
};

export const ContactCardForm = () => {
  const { isEditing, assetDetails: contactCardDetails } = useContactCardContext();
  return (
    <ContactCardFormWrapper data-test="contact-card-form">
      <ContactCardHeaderCard>
        <DisplayNameWrapper>
          {contactCardDetails?.displayName || 'New Contact Card'}
        </DisplayNameWrapper>
        {getContactCardAvatar(contactCardDetails as ContactCard, AVATAR_SIZES.XLARGE)}
      </ContactCardHeaderCard>
      <div>{isEditing ? <ContactCardEditable /> : <ContactCardDetails />}</div>
    </ContactCardFormWrapper>
  );
};
